<template>
  <div class="app-container">
    <pre-interview-form v-if="!formSubmitted" @form-submitted="handleFormSubmitted" />
    <div v-else class="chatbot">
      <div class="renew2_wrapper">
        <div class="button-row">
          <!-- 戻るボタン -->
          <button @click="goBack" class="back-button" aria-label="前の画面に戻る">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <!-- リロードボタン -->
          <button @click="reloadChat" class="reload-button" aria-label="チャットをリロード">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </button>
        </div>

        <div class="messages" ref="messageContainer">
          <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.sender]">
            <div class="bubble" v-html="formatMessage(msg.text)"></div>
          </div>
          <div v-if="isTyping" class="message bot">
            <div class="bubble typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
        <div class="input-container">
          <textarea class="user-input-textarea" v-model="userInput" @compositionstart="isComposing = true"
            @compositionend="isComposing = false" placeholder="メッセージを入力..." :disabled="isTyping" rows="1"
            ref="textarea"></textarea>
          <button @click="sendMessage" class="send-button" :disabled="isTyping || !userInput.trim()">
            送信
          </button>
        </div>
      </div>
      <InterviewSheet
        ref="interviewSheet"
        v-if="formSubmitted"
        :companyInfo="companyInfoLocal"
        :motivation="motivationLocal"
       />
    </div>
  </div>
</template>

<script>
import PreInterviewForm from './PreInterviewForm.vue'
import InterviewSheet from './InterviewSheet.vue';

export default {
  name: 'ChatbotComponent',
  components: {
    PreInterviewForm,
    InterviewSheet
  },
  props: {
    companyInfo: {
      type: String,
      required: false
    },
    motivation: {
      type: String,
      required: false
    },
    previousMessages: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      userInput: '',
      messages: [],
      isComposing: false,
      isTyping: false,
      formSubmitted: false,
      companyInfoLocal: '',
      motivationLocal: '',
      previousMessagesLocal: '',
    }
  },
  created() {
    // previousMessages の処理
    if (typeof this.previousMessages === 'string') {
      try {
        // シングルクォートをダブルクォートに置換してパース
        const jsonString = this.previousMessages.replace(/'/g, '"');
        this.previousMessagesLocal = JSON.parse(jsonString);
      } catch (error) {
        console.error('previousMessages のパース中にエラーが発生しました:', error);
        this.previousMessagesLocal = [];
      }
    } else {
      this.previousMessagesLocal = this.previousMessages || [];
    }

    // messages を正しい形式に変換
    this.messages = this.previousMessagesLocal.map(msg => ({
      sender: msg.role === 'assistant' ? 'bot' : 'user',
      text: msg.content
    }));

    this.companyInfoLocal = this.companyInfo || '';
    this.motivationLocal = this.motivation || '';
  },
  watch: {
    // userInputの変更を監視し、テキストエリアの高さを調整
    userInput() {
      this.$nextTick(this.adjustTextareaHeight)
    },
    companyInfo(newValue) {
      this.companyInfoLocal = newValue;
    },
    motivation(newValue) {
      this.motivationLocal = newValue;
    },
  },
  computed: {
    greetingMessage() {
      let message = 'こんにちは！AI面接練習(β版)へようこそ。\n\n'
      message += '私はあなたの面接練習をサポートするAIヤギです。実際の面接官の役割を演じながら、様々な質問をさせていただきます。\n\n'

      if (this.companyInfoLocal && this.motivationLocal) {
        message += `あなたが志望している企業についての情報と志望動機を拝見しました。\n\n`
        message += `企業情報: ${this.companyInfoLocal}\n`
        message += `志望動機: ${this.motivationLocal}\n\n`
        message += 'これらの情報を踏まえて、面接練習を進めていきます。\n\n'
      } else if (this.companyInfoLocal) {
        message += `あなたが志望している企業についての情報を拝見しました。\n\n`
        message += `企業情報: ${this.companyInfoLocal}\n\n`
        message += 'これを踏まえて、面接練習を進めていきます。\n\n'
      } else if (this.motivationLocal) {
        message += `あなたの志望動機を拝見しました。\n\n`
        message += `志望動機: ${this.motivationLocal}\n\n`
        message += 'これを踏まえて、面接練習を進めていきます。\n\n'
      }

      message += '準備はよろしいでしょうか？それでは、面接練習を始めましょう。'

      return message
    },
    firstQuestion() {
      return '最初の質問です。\n\n1.簡単な自己紹介をお願いできますか？'
    }
  },
  mounted() {
    window.addEventListener('resize', this.adjustMessagesHeight)
    this.adjustMessagesHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustMessagesHeight)
  },
  updated() {
    this.scrollToBottom()
  },
  methods: {
    handleFormSubmitted (formData) {
      this.companyInfoLocal = formData.companyInfo
      this.motivationLocal = formData.motivation
      this.previousMessagesLocal = formData.previousMessages
      this.formSubmitted = true
      this.showGreetingWithDelay()
    },
    async sendMessage() {
      if (this.userInput.trim() === '') return
      this.messages.push({ sender: 'user', text: this.userInput })
      this.userInput = ''
      this.isTyping = true
      const apiMessages = this.messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }))
      try {
        const response = await this.$axios.post(this.$varuna.endPoints.aiPracticeChat, {
          prompt: apiMessages,
          uuid: this.$firebase.user.uid,
          companyInfo: this.companyInfoLocal,
          reason: this.motivationLocal,
        })
        const reply = response.data.reply
        if (reply) {
          await this.typeMessage('bot', reply)
        }
      } catch (error) {
        console.error(error)
        await this.typeMessage('bot', 'エラーが発生しました。')
      }
      this.isTyping = false
    },
    goBack() {
      this.formSubmitted = false
      this.messages = []
    },
    reloadChat() {
      console.log('Reloading chat...')
      this.messages = []
      this.previousMessagesLocal = []
      this.userInput = ''
      this.showGreetingWithDelay()
    },
    showGreetingWithDelay () {
      this.isTyping = true;

      // previousMessagesLocalが存在する場合は、greetingMessageをスキップして会話履歴を表示
      if (this.previousMessagesLocal && this.previousMessagesLocal.length > 0) {
        // 文字列をオブジェクトに変換
        
        // 会話履歴を順番に表示
        this.previousMessagesLocal.forEach(message => {
          if (message.role === 'assistant') {
            this.typeMessage('bot', message.content);
          } else if (message.role === 'user') {
            this.typeMessage('user', message.content);
          }
        });

        // 画面のスクロールをトップにして、最新のメッセージを表示
        window.scrollTo(0, 0);
      } else {
        // previousMessagesLocalが存在しない場合はgreetingMessageを表示
        
        setTimeout(async () => {
          await this.typeMessage('bot', this.greetingMessage);
          setTimeout(() => {
            this.typeMessage('bot', this.firstQuestion);
          }, 1000);
        }, 1000);
      }
    },
    async typeMessage(sender, text) {
      if (sender === 'bot') {
        this.isTyping = true
      }

      this.messages.push({
        sender: sender,
        text: text
      })

      this.$nextTick(() => this.scrollToBottom())

      if (sender === 'bot') {
        this.isTyping = false
      }
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    adjustMessagesHeight() {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    },
    formatMessage(text) {
      // 改行文字を<br>タグに置換
      return text.replace(/\n/g, '<br>')
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.renew2_wrapper {
  width: 768px;
  margin: 0 auto 50px;
}

.chatbot {
  width: 100%;
  position: relative;
  background: #FFFFE8;
  padding: 10px 0;
}

.messages {
  height: calc(100vh - 340px);
  overflow-y: scroll;
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 5px;
}

.message {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.bubble {
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
  color: white;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.4;
}

.user {
  align-items: flex-end;
}

.user .bubble {
  background: #0F9934;
  border-bottom-right-radius: 4px;
}

.bot {
  align-items: flex-start;
}

.bot .bubble {
  background: #EFF5E9;
  color: #404040;
  border-bottom-left-radius: 4px;
}

.input-container {
  display: flex;
  margin-top: 10px;
  align-items: flex-end;
  /* テキストエリアとボタンを下揃えに */
}

.user-input-textarea {
  flex-grow: 1;
  padding: 10px;
  background-color: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 20px;
  color: #606266;
  font-size: inherit;
  min-height: 40px;
  max-height: 120px;
  /* 最大高さを設定 */
  resize: none;
  /* リサイズハンドルを非表示に */
  overflow-y: auto;
  /* 内容が多い場合はスクロール可能に */
  box-sizing: border-box;
  line-height: 1.4;
  display: block;
  /* テキストエリアをブロック要素として扱う */
  margin-right: 10px;
}

.send-button {
  padding: 10px 20px;
  background-color: #0084FF;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: inherit;
  transition: background-color 0.3s;
  max-height: 40px;
  /* 最小の高さを設定 */
}

.send-button:hover {
  background-color: #0066CC;
}

.send-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.button-row {
  display: flex;
  justify-content: space-between;
  /* 左右にボタンを配置 */
}

.back-button,
.reload-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.3s ease;
  margin: 0;
}

.reload-button:hover {
  transform: rotate(360deg);
}

.back-button:hover {
  transform: translateX(-10px);
}

.back-button svg,
.reload-button svg {
  width: 24px;
  height: 24px;
}

.reload-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.3s ease;
}

.reload-button:hover {
  transform: rotate(180deg);
}

.reload-button svg {
  width: 24px;
  height: 24px;
}

.typing-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 15px;
}

.typing-indicator span {
  height: 10px;
  width: 10px;
  float: left;
  margin: 0 1px;
  background-color: #9E9EA1;
  display: block;
  border-radius: 50%;
  opacity: 0.4;
}

.typing-indicator span:nth-of-type(1) {
  animation: 1s blink infinite 0.3333s;
}

.typing-indicator span:nth-of-type(2) {
  animation: 1s blink infinite 0.6666s;
}

.typing-indicator span:nth-of-type(3) {
  animation: 1s blink infinite 0.9999s;
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

@media (max-width: 768px) {

  .messages {
    height: calc(100vh - 250px);
  }

  h1 {
    font-size: 24px;
  }

  p {
    font-size: 14px;
  }

  .bubble {
    font-size: 13px;
    padding: 8px 12px;
  }

  .user-input-textbox,
  .send-button {
    font-size: 14px;
    height: 36px;
  }

  .reload-button {
    top: 5px;
    right: 5px;
  }

  .reload-button svg {
    width: 20px;
    height: 20px;
  }
}
</style>
